import type { ScheduleReminder } from '@/types/cycle-evaluation/CycleSchedule';
import type { Descendant } from 'slate';

interface isScheduleReminderCheckedParams {
    scheduleReminder?: ScheduleReminder[];
    daysBeforeDeadline: number;
}

interface MergeScheduleRemindersParams {
    storedReminders?: ScheduleReminder[];
    reminderSevenDays?: boolean;
    reminderThreeDays?: boolean;
    reminderOnDeadline?: boolean;
}

interface IsReminderOffsetWithinCompletionWindowParams {
    daysBeforeDeadline: number;
    daysAllowedToComplete?: number;
}

interface GetEffectiveDaysAllowedToCompleteParams<TStored extends number | undefined> {
    daysToComplete?: number;
    storedDaysAllowedToComplete: TStored;
}

interface GetReminderOffsetsOutsideCompletionWindowParams {
    scheduleReminders: readonly ScheduleReminder[];
    daysAllowedToComplete?: number;
}

export const OFFERED_REMINDER_DAYS_BEFORE_DEADLINE = [7, 3, 0] as const;

export const getEmptyEmailMessage = (): Descendant[] => [
    { type: 'paragraph', children: [{ text: '' }] },
];

export const isScheduleReminderChecked = ({
    scheduleReminder,
    daysBeforeDeadline,
}: isScheduleReminderCheckedParams) => {
    return scheduleReminder?.some((reminder) => reminder.daysBeforeDeadline === daysBeforeDeadline);
};

export const isOfferedReminderDay = (daysBeforeDeadline: number): boolean =>
    OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.some((offered) => offered === daysBeforeDeadline);

/**
 * Mirrors `ScheduleReminderOffsetRules.FiresWithinTheCompletionWindow` in the back end. The two
 * have to stay identical: this one closes the option, that one refuses the request.
 */
export const isReminderOffsetWithinCompletionWindow = ({
    daysBeforeDeadline,
    daysAllowedToComplete,
}: IsReminderOffsetWithinCompletionWindowParams): boolean =>
    daysAllowedToComplete === undefined ||
    (daysBeforeDeadline >= 0 && daysBeforeDeadline <= daysAllowedToComplete);

/**
 * The one fallback shared by the outgoing payload and the local refusal recompute: an empty
 * form field defers to whatever window the schedule already holds, never to an unlimited one
 * that would hide every offending delay behind the generic error.
 */
export const getEffectiveDaysAllowedToComplete = <TStored extends number | undefined>({
    daysToComplete,
    storedDaysAllowedToComplete,
}: GetEffectiveDaysAllowedToCompleteParams<TStored>): number | TStored =>
    daysToComplete ?? storedDaysAllowedToComplete;

export const getReminderOffsetsOutsideCompletionWindow = ({
    scheduleReminders,
    daysAllowedToComplete,
}: GetReminderOffsetsOutsideCompletionWindowParams): number[] =>
    scheduleReminders
        .map(({ daysBeforeDeadline }) => daysBeforeDeadline)
        .filter(
            (daysBeforeDeadline) =>
                !isReminderOffsetWithinCompletionWindow({
                    daysBeforeDeadline,
                    daysAllowedToComplete,
                }),
        );

export const mergeScheduleReminders = ({
    storedReminders,
    reminderSevenDays,
    reminderThreeDays,
    reminderOnDeadline,
}: MergeScheduleRemindersParams): ScheduleReminder[] => {
    const stored = storedReminders ?? [];
    const isCheckedByDays: Record<number, boolean> = {
        7: Boolean(reminderSevenDays),
        3: Boolean(reminderThreeDays),
        0: Boolean(reminderOnDeadline),
    };

    const carriedOverReminders = stored
        .filter((reminder) => !isOfferedReminderDay(reminder.daysBeforeDeadline))
        .map(({ id, daysBeforeDeadline }) => ({ id, daysBeforeDeadline }));

    const checkedReminders = OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.filter(
        (days) => isCheckedByDays[days],
    ).map((days) => ({
        id: stored.find((reminder) => reminder.daysBeforeDeadline === days)?.id ?? '',
        daysBeforeDeadline: days,
    }));

    return [...carriedOverReminders, ...checkedReminders];
};
